import React, { useState } from 'react';
import Modal, { Footer } from 'components/common/Modal';
import showToast from 'components/common/Toast';
import { Form } from 'react-final-form';
import I18n from '@/modules/i18n';
import IAssignment from '@/components/interfaces/Assignment';
import { NumberField, CheckboxField, StandardField } from 'components/common/Forms';
import Axios from 'axios';
import { denormalizedScore, normalizedScore } from '@/components/staffer/Grading/utils';
import { ScoreData } from './types';
import styles from './BulkGradingModal.module.scss';

export type BulkGradingScoreMode = 'points' | 'credit';

interface BulkGradingModalProps {
  assignment?: IAssignment;
  closeModal: () => void;
  isOpen: boolean;
  onComplete?: () => void;
  saveScore?: (row: ScoreData, grade: number | string) => Promise<unknown>;
  scoreMode?: BulkGradingScoreMode;
  scoresData: ScoreData[];
}

const putScore = (row: ScoreData, grade: number | string) => (
  Axios.put(row.updateURL, { notebook_result: { grade }, score: grade })
);

const BulkGradingModal = ({
  assignment,
  closeModal,
  isOpen,
  onComplete = () => window.location.reload(),
  saveScore = putScore,
  scoreMode = 'points',
  scoresData,
}: BulkGradingModalProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasError, setHasError] = useState(false);

  if (!scoresData.length) return null;

  const isCreditMode = scoreMode === 'credit';
  const maxScore = assignment ? normalizedScore(assignment, scoresData[0].maxScore) : scoresData[0].maxScore;

  const unansweredQuestions = scoresData.filter(sd => (!sd.questionAnswered && sd.updateURL));
  const answeredQuestions = scoresData.filter(sd => (sd.questionAnswered && sd.updateURL));

  const errorStatus = (status, text) => {
    setHasError(status);
    setError(text);
  };

  const validations = async (values) => {
    if (Object.keys(values).length === 0 || (!values.answeredQuestions && !values.unansweredQuestions)) {
      errorStatus(true, I18n.t('bulk_grade_choose_step_one'));
      return true;
    }
    if (isCreditMode) {
      if (!values.credit) {
        errorStatus(true, I18n.t('bulk_grade_credit_required'));
        return true;
      }
      setHasError(false);
      return false;
    }
    if (!values.score) {
      errorStatus(true, I18n.t('bulk_grade_score_required'));
      return true;
    }
    if ((values.score * 10) % 1 !== 0) {
      errorStatus(true, I18n.t('bulk_grade_score_decimal'));
      return true;
    }
    if (values.score > maxScore || values.score < 0) {
      errorStatus(true, I18n.t('bulk_grade_score_range', { max: maxScore }));
      return true;
    }
    setHasError(false);
    return false;
  };

  const setErrors = () => {
    setSaving(false);
    setError(I18n.t('bulk_grade_save_error'));
    setHasError(true);
  };

  const onSubmit = async (data) => {
    const inputError = await validations(data);
    if (inputError) return;

    const questionsToGrade = [];
    if (data.answeredQuestions) {
      questionsToGrade.push(...answeredQuestions);
    }
    if (data.unansweredQuestions) {
      questionsToGrade.push(...unansweredQuestions);
    }

    const pointsGrade = assignment ? denormalizedScore(assignment, data.score) : data.score;
    // Credit mode sends each question's own raw max, so there is nothing to denormalize.
    const gradeFor = (row: ScoreData) => {
      if (!isCreditMode) return pointsGrade;

      return data.credit === 'full' ? row.maxScore : 0;
    };

    setSaving(true);
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const question of questionsToGrade) {
        // eslint-disable-next-line no-await-in-loop
        await saveScore(question, gradeFor(question));
      }
    } catch (e) {
      setErrors();
      return;
    }

    setSaving(false);
    closeModal();
    onComplete();
    showToast(I18n.t('bulk_grade_success', { count: questionsToGrade.length }), { autoClose: 5000 });
  };

  const selectedCount = values => (
    (values.answeredQuestions ? answeredQuestions.length : 0)
    + (values.unansweredQuestions ? unansweredQuestions.length : 0)
  );

  return (
    <Modal
      closeModal={closeModal}
      headerText={I18n.t('bulk_grade_student_work')}
      isOpen={isOpen}
    >
      <Form
        render={({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit}>
            <div>
              <strong>{I18n.t('bulk_grade_step_one')}</strong>
              {' '}
              {I18n.t(isCreditMode ? 'bulk_grade_select_work_for_student' : 'bulk_grade_select_work_for_question')}
            </div>
            <div className={styles.formGroup}>
              <div className={styles.formField}>
                <CheckboxField
                  disabled={answeredQuestions.length === 0}
                  label={I18n.t('answered')}
                  labelPos="right"
                  name="answeredQuestions"
                />
              </div>
              <div className={styles.formField}>
                <CheckboxField
                  disabled={unansweredQuestions.length === 0}
                  label={I18n.t('unanswered')}
                  labelPos="right"
                  name="unansweredQuestions"
                />
              </div>
            </div>

            <div>
              <strong>{I18n.t('bulk_grade_step_two')}</strong>
              {' '}
              {I18n.t('bulk_grade_set_the_score')}
            </div>
            <div className={styles.formGroup}>
              {isCreditMode ? (
                <>
                  <div className={styles.formField}>
                    <StandardField
                      element="input"
                      id="credit_none"
                      label={I18n.t('no_credit')}
                      labelPos="right"
                      name="credit"
                      type="radio"
                      value="none"
                    />
                  </div>
                  <div className={styles.formField}>
                    <StandardField
                      element="input"
                      id="credit_full"
                      label={I18n.t('full_credit')}
                      labelPos="right"
                      name="credit"
                      type="radio"
                      value="full"
                    />
                  </div>
                </>
              ) : (
                <div className={styles.formField}>
                  <NumberField
                    className={styles.scoreInput}
                    label={`/ ${maxScore}`}
                    labelPos="right"
                    name="score"
                  />
                </div>
              )}
            </div>

            {hasError && <div className={styles.error}>{error}</div>}

            {selectedCount(values) > 0 && (
              <span className="tw-flex tw-justify-end tw-pr-4">
                <i aria-hidden="true" className="fa fa-exclamation-triangle tw-text-orange" />
                &nbsp;{I18n.t('bulk_grade_answers_will_be_graded', { count: selectedCount(values) })}
              </span>
            )}

            <Footer
              primaryButtonText={I18n.t('bulk_grade')}
              primiaryButtonDisabled={hasError === true || saving === true}
              secondaryButtonCallback={closeModal}
            />
          </form>
        )}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default BulkGradingModal;
